import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import { selectors } from "reducers";
import { Form, Formik, Field } from "formik";
import { useFetch } from "hooks";
import { path } from "ramda";
import { get, put } from "utils/api";
import FormikField from "components/forms/formik-field";
import { InlineWaiting, Spinner } from "components/ui";
import "./../tabsStyles.css";
import PatientAppointments from "../appointments/index";
import { invalidatePatientProfileInfo, fetchPatientProfileInfo } from "actions/patient";
import { getApiErrorMessage } from "utils/misc";
import * as msg from "actions/message";

const OnboardingData = ({
  patientId,
  companyManagesOnboarding,
  companyManagesCompliance,
  companyManagesResupply,
  inactivationReasons
}) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [ onboardingModified, setOnboardingModified] = useState(false);
  const [ arrivingValues, setArrivingValues ] = useState({
    can_edit: false,
    machine_received: false,
    scheduled_completed: "",
    setup_completed: "",
    scheduled_pickup_task: false,
    setup_pickup_task: false,
    scheduled_next_stage: "",
    setup_next_stage: "",
    not_scheduled_reason: "",
    not_setup_reason: "",
  });
  const [ formInactivationReasons, setFormInactivationReasons ] = useState([]);
  useEffect(() => {
      setFormInactivationReasons(inactivationReasons.filter(x => x.text === "Unable to Reach Patient" || x.text === "Patient Refused"));
  }, [inactivationReasons]);
  const scheduledChange = (value) => {
    if (value === "") {
      // blank all options
      formRef.current.setFieldValue("machine_received", false);
      formRef.current.setFieldValue("not_scheduled_reason", "");
      formRef.current.setFieldValue("scheduled_next_stage", "");
      formRef.current.setFieldValue("setup_completed", "");
      formRef.current.setFieldValue("setup_pickup_task", false);
      formRef.current.setFieldValue("scheduled_pickup_task", false);
      formRef.current.setFieldValue("not_setup_reason", "");
      formRef.current.setFieldValue("setup_next_stage", "");
    }
    if (value === "true") {
      // blank 3 no options
      formRef.current.setFieldValue("not_scheduled_reason", "");
      formRef.current.setFieldValue("scheduled_next_stage", "");
      formRef.current.setFieldValue("scheduled_pickup_task", false);
      // blank all setup options
      formRef.current.setFieldValue("setup_completed", '');
      formRef.current.setFieldValue("setup_pickup_task", false);
      formRef.current.setFieldValue("not_setup_reason", "");
      formRef.current.setFieldValue("setup_next_stage", "");
    }
    if (value === "false") {
      // blank 1 yes options
      formRef.current.setFieldValue("machine_received", false);
      //blank all setup options
      formRef.current.setFieldValue("setup_completed", "");
      formRef.current.setFieldValue("setup_pickup_task", false);
      formRef.current.setFieldValue("not_setup_reason", "");
      formRef.current.setFieldValue("setup_next_stage", "");
    }
    formRef.current.setFieldValue("scheduled_completed", value);
    setOnboardingModified(true);
  };

  const setupCompletedChange = value => {
    if (value === "") {
      // blank all setup completed options
      formRef.current.setFieldValue("setup_next_stage", "");
      formRef.current.setFieldValue("setup_pickup_task", false);
      formRef.current.setFieldValue("not_setup_reason", "");
    }
    if (value === "true") {
      // blank 3 no options
      formRef.current.setFieldValue("setup_next_stage", "");
      formRef.current.setFieldValue("not_setup_reason", "");
      formRef.current.setFieldValue("setup_pickup_task", false);
    }
    if (value === "false") {
      // blank 1 yes options
      formRef.current.setFieldValue("setup_next_stage", "");
      formRef.current.setFieldValue("scheduled_pickup_task", false);
    }
    formRef.current.setFieldValue("setup_completed", value);
    setOnboardingModified(true);
  };
    
  const validateNullAndRequiredIfChanged = value => {
    let errorMessage;
    if (!value && onboardingModified) {
      errorMessage = "You must select an option";
    }
    return errorMessage;
  };

  const { fetchData: uploadOnboardingChanges } = useFetch({
    apiFn: ({ id, info }) =>
      put(`patients/${id}/onboarding-info`, info),
    defaultValue: null,
    transformError: path(["response", "body", "status"]),
    onSuccess: () => {
      dispatch(msg.message("Success!"));
    },
    onError: error => dispatch(msg.errorMessage(getApiErrorMessage(error)))
  });

    const { response: onboardingInfo, fetchData, isFetching: fetchingOnboardingInfo } = useFetch({
      apiFn: patientId => get(`patients/${patientId}/onboarding-info`),
      defaultValue: [],
      transformError: path(["response", "body", "status"])
    });

    useEffect(() => {
      fetchData(patientId);
    }, []);

    useEffect(() => {
      let setup_completed_value = "";
      let scheduled_completed_value = "";
      switch(onboardingInfo.scheduled_completed)
      {
        case true:
          scheduled_completed_value = "true";
          break;
        case false:
          scheduled_completed_value = "false";
          break;
        case null:
          scheduled_completed_value = "";
          break;
      }
      switch(onboardingInfo.setup_completed)
      {
        case true:
          setup_completed_value = "true";
          break;
        case false:
          setup_completed_value = "false";
          break;
        case null:
          setup_completed_value = "";
          break;
      }
      const _onboardingInfoCopy = {
        can_edit: onboardingInfo.can_edit ?? false,
        machine_received: onboardingInfo.machine_received ?? false,
        scheduled_completed: scheduled_completed_value,
        setup_completed: setup_completed_value,
        scheduled_pickup_task: onboardingInfo.scheduled_pickup_task ?? false,
        setup_pickup_task: onboardingInfo.setup_pickup_task ?? false,
        scheduled_next_stage: onboardingInfo.scheduled_next_stage ?? "",
        setup_next_stage: onboardingInfo.setup_next_stage ?? "",
        not_scheduled_reason: onboardingInfo.not_scheduled_reason ?? "",
        not_setup_reason: onboardingInfo.not_setup_reason ?? "",
      };
      setArrivingValues(_onboardingInfoCopy);
    }, [onboardingInfo]);

  return (
    <>
      <div>
        <PatientAppointments patientId={patientId} onboardingAppointmentType={true} coachFilter="OnboardingCoach" />
      </div>
      <div className="special-fields">
        {fetchingOnboardingInfo ? (
          <InlineWaiting reason="Fetching onboarding data" />
        ) : (
          <div>
            <Formik
              initialValues={{
                can_edit: arrivingValues.can_edit,
                scheduled_completed: arrivingValues.scheduled_completed,
                setup_completed: arrivingValues.setup_completed,
                machine_received: arrivingValues.machine_received,
                not_scheduled_reason: arrivingValues.not_scheduled_reason,
                scheduled_next_stage: arrivingValues.scheduled_next_stage,
                scheduled_pickup_task: arrivingValues.scheduled_pickup_task,
                setup_pickup_task: arrivingValues.setup_pickup_task,
                not_setup_reason: arrivingValues.not_setup_reason,
                setup_next_stage: arrivingValues.setup_next_stage
              }}
              innerRef={formRef}
              enableReinitialize={true}
              onSubmit={async values => {
                await uploadOnboardingChanges({ id: patientId, info: values }).then(
                  () => {
                    dispatch(
                      invalidatePatientProfileInfo({ patientId: patientId })
                    );
                    dispatch(fetchPatientProfileInfo({ patientId: patientId }));
                  }
                );
              }}
            >
              {({ values, handleChange, isSubmitting }) => {
                return (
                  <Form className="onboaring-data-width">
                    <div style={{ fontSize: "1rem" }}>
                      Patient Scheduled?
                      <label
                        style={{
                          display: "inline-block",
                          marginLeft: "0.5rem",
                          marginRight: "1rem"
                        }}
                      >
                        Yes
                      </label>
                      <Field
                        type="radio"
                        name="scheduled_completed"
                        value="true"
                        disabled={
                          values.can_edit === false ||
                          isSubmitting ||
                          !companyManagesOnboarding
                        }
                        onClick={e => {
                          if (e.target.value === values.scheduled_completed) {
                            e.target.value = "";
                          }
                          handleChange(e);
                          scheduledChange(e.target.value);
                        }}
                      />
                      <label
                        style={{
                          display: "inline-block",
                          marginLeft: "0.5rem",
                          marginRight: "1rem"
                        }}
                      >
                        No
                      </label>
                      <Field
                        type="radio"
                        name="scheduled_completed"
                        value="false"
                        onClick={e => {
                          if (e.target.value === values.scheduled_completed) {
                            e.target.value = "";
                          }
                          handleChange(e);
                          scheduledChange(e.target.value);
                        }}
                        disabled={
                          values.can_edit === false ||
                          isSubmitting ||
                          !companyManagesOnboarding
                        }
                      />
                    </div>

                    <div
                      style={{
                        display:
                          values.scheduled_completed === "true"
                            ? "unset"
                            : "none"
                      }}
                    >
                      <fieldset>
                        <legend>Scheduling Patient</legend>
                        <FormikField
                          name="machine_received"
                          label="Patient Received Machine"
                          type="checkbox"
                          disabled={
                            values.can_edit === false ||
                            isSubmitting ||
                            values.can_edit === false ||
                            values.setup_next_stage === "Remain Active"
                          }
                          onChange={e => {
                            handleChange(e);
                          }}
                          style={{
                            display: "inline-flex",
                            alignSelf: "center"
                          }}
                        />
                      </fieldset>
                    </div>

                    <div
                      style={{
                        display:
                          values.scheduled_completed === "false"
                            ? "unset"
                            : "none"
                      }}
                    >
                      <fieldset>
                        <legend>Not Scheduling Patient</legend>
                        <span>
                          Please select one of the following reasons why the
                          patient was not scheduled:
                        </span>
                        <FormikField
                          name="not_scheduled_reason"
                          component="select"
                          validate={
                            values.scheduled_completed === "false"
                              ? validateNullAndRequiredIfChanged
                              : null
                          }
                          disabled={
                            values.can_edit === false ||
                            isSubmitting ||
                            values.scheduled_completed !== "false"
                          }
                          onChange={e => {
                            handleChange(e);
                          }}
                        >
                          <option key="null" value=""></option>
                          {formInactivationReasons.map(x => (
                            <option
                              key={x.value}
                              value={x.value}
                            >
                              {x.text}
                            </option>
                          ))}
                        </FormikField>
                        <span>
                          Would you like to deactivate the patient with the
                          above reason selected:
                        </span>
                        <FormikField
                          name="scheduled_next_stage"
                          component="select"
                          validate={
                            values.scheduled_completed === "false"
                              ? validateNullAndRequiredIfChanged
                              : null
                          }
                          disabled={
                            values.can_edit === false ||
                            isSubmitting ||
                            values.scheduled_completed !== "false"
                          }
                          onChange={e => {
                            handleChange(e);
                          }}
                        >
                          <option key="null" value=""></option>
                          <option key="Inactivate" value={"Inactivate"}>
                            Inactivate
                          </option>
                          <option key="Remain Active" value={"Remain Active"}>
                            Remain Active
                          </option>
                        </FormikField>
                        <div
                          style={{
                            display:
                              values.scheduled_completed === "false" &&
                              values.setup_completed === ""
                                ? "unset"
                                : "none"
                          }}
                        >
                          <FormikField
                            name="scheduled_pickup_task"
                            label="Schedule machine pickup:"
                            type="checkbox"
                            disabled={
                              values.can_edit === false ||
                              isSubmitting ||
                              values.setup_next_stage === "Remain Active"
                            }
                            onChange={e => {
                              handleChange(e);
                            }}
                            style={{
                              display: "inline-flex",
                              alignSelf: "center"
                            }}
                          />
                        </div>
                      </fieldset>
                    </div>

                    <div
                      style={{
                        display:
                          values.scheduled_completed === "true"
                            ? "unset"
                            : "none"
                      }}
                    ></div>
                    <div style={{ fontSize: "1rem" }}>
                      Setup Completed?
                      <label
                        style={{
                          display: "inline-block",
                          marginLeft: "0.5rem",
                          marginRight: "1rem",
                          fontSize: "1rem"
                        }}
                      >
                        Yes
                      </label>
                      <Field
                        type="radio"
                        name="setup_completed"
                        value="true"
                        onClick={e => {
                          if (e.target.value === values.setup_completed) {
                            e.target.value = "";
                          }
                          handleChange(e);
                          setupCompletedChange(e.target.value);
                        }}
                        disabled={
                          values.can_edit === false ||
                          isSubmitting ||
                          !companyManagesOnboarding
                        }
                      />
                      <label
                        style={{
                          display: "inline-block",
                          marginLeft: "0.5rem",
                          marginRight: "1rem",
                          fontSize: "1rem"
                        }}
                      >
                        No
                      </label>
                      <Field
                        type="radio"
                        name="setup_completed"
                        value="false"
                        onClick={e => {
                          if (e.target.value === values.setup_completed) {
                            e.target.value = "";
                          }
                          handleChange(e);
                          setupCompletedChange(e.target.value);
                        }}
                        disabled={
                          values.can_edit === false ||
                          isSubmitting ||
                          !companyManagesOnboarding
                        }
                      />
                    </div>

                    <div
                      style={{
                        display:
                          values.setup_completed === "false" ? "unset" : "none"
                      }}
                    >
                      <fieldset>
                        <legend>Patient Setup Not Completed</legend>
                        <span>
                          Please select one of the following reasons why setup
                          was not completed for the patient:
                        </span>
                        <FormikField
                          name="not_setup_reason"
                          component="select"
                          validate={
                            values.setup_completed === "false"
                              ? validateNullAndRequiredIfChanged
                              : null
                          }
                          disabled={
                            values.can_edit === false ||
                            isSubmitting ||
                            values.setup_completed !== "false"
                          }
                          onChange={e => {
                            handleChange(e);
                          }}
                        >
                          <option key="null" value=""></option>
                          {formInactivationReasons.map(x => (
                            <option
                              key={x.value}
                              value={x.value}
                            >
                              {x.text}
                            </option>
                          ))}
                        </FormikField>
                        <span>
                          Would you like to deactivate the patient with the
                          above reason selected:
                        </span>
                        <FormikField
                          name="setup_next_stage"
                          component="select"
                          validate={
                            values.setup_completed === "false"
                              ? validateNullAndRequiredIfChanged
                              : null
                          }
                          disabled={
                            values.can_edit === false ||
                            isSubmitting ||
                            values.setup_completed !== "false"
                          }
                          onChange={e => {
                            handleChange(e);
                          }}
                        >
                          <option key="0" value=""></option>
                          <option key="Inactivate" value={"Inactivate"}>
                            Inactivate
                          </option>
                          <option key="Remain Active" value={"Remain Active"}>
                            Remain Active
                          </option>
                        </FormikField>
                        <div
                          style={{
                            display:
                              values.setup_completed === "false"
                                ? "unset"
                                : "none"
                          }}
                        >
                          <FormikField
                            name="setup_pickup_task"
                            label="Schedule machine pickup:"
                            type="checkbox"
                            disabled={
                              values.can_edit === false ||
                              isSubmitting
                            }
                            onChange={e => {
                              handleChange(e);
                            }}
                            style={{
                              display: "inline-flex",
                              alignSelf: "center"
                            }}
                          />
                        </div>
                      </fieldset>
                    </div>

                    <div
                      style={{
                        display:
                          values.setup_completed === "true" ? "unset" : "none"
                      }}
                    >
                      <fieldset>
                        <legend>Patient Setup Completed</legend>
                        <span>
                          Please select the next stage to assign to this
                          patient.
                        </span>
                        <FormikField
                          name="setup_next_stage"
                          component="select"
                          validate={
                            values.setup_completed === "true"
                              ? validateNullAndRequiredIfChanged
                              : null
                          }
                          disabled={
                            values.can_edit === false ||
                            isSubmitting ||
                            values.setup_completed !== "true"
                          }
                          onChange={e => {
                            handleChange(e);
                          }}
                        >
                          <option value="" key="0"></option>
                          {companyManagesCompliance ? (
                            <option value="Compliance" key="2">
                              Compliance
                            </option>
                          ) : (
                            ""
                          )}
                          {companyManagesResupply ? (
                            <option value="Resupply" key="1">
                              Resupply
                            </option>
                          ) : (
                            ""
                          )}
                        </FormikField>
                      </fieldset>
                    </div>

                    <div className="submit-button-container">
                      <button
                        className="submit-button"
                        type="submit"
                        disabled={
                          values.can_edit === false ||
                          isSubmitting ||
                          ((values.scheduled_completed === null ||
                            values.scheduled_completed === undefined) &&
                            (values.setup_completed === null ||
                              values.setup_completed === undefined)) ||
                          !companyManagesOnboarding
                        }
                      >
                        Submit
                      </button>
                      {isSubmitting && <Spinner />}
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        )}
      </div>
    </>
  );
};

OnboardingData.propTypes = {
  patientId: PropTypes.string.isRequired,
  companyManagesOnboarding: PropTypes.bool,
  companyManagesCompliance: PropTypes.bool,
  companyManagesResupply: PropTypes.bool,
  inactivationReasons: PropTypes.arrayOf(PropTypes.object)
};

export default connect(state => ({
    inactivationReasons: selectors.getEnumInactivationReasons(state)
  })
)(OnboardingData);
